// Change #100326 - Settings > WhatsApp > Channels (one Twilio WhatsApp sender each).
//
// Secrets never come back from the API: the list carries AuthUsernameMasked and
// FlagAuthConfigured instead of the Account SID and auth token. So on edit both credential
// inputs start blank and are only sent when something is typed - the backend keeps the stored
// auth token on a blank AuthPassword, and we never send a blank AuthUsername (it would wipe
// the stored SID).
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  createChannel,
  deleteChannel,
  errorText,
  listStaff,
  listTemplates,
  ok,
  testChannel,
  updateChannel,
} from "../../../../Components/WhatsApp/whatsappApi";
import BusinessHoursEditor, { normaliseBusinessHours, validateBusinessHours } from "./BusinessHoursEditor";
import { ActiveBadge, FieldHelp, ListState, Switch, Toolbar, isOn, parseJson } from "./shared";

export const AI_MODES = {
  OFF: { label: "Off", help: "No AI on this number." },
  SUGGEST: { label: "Suggest", help: "Agents get an AI suggest button that drafts a reply for them to check and send." },
  AUTO: { label: "Auto reply", help: "New conversations are replied to automatically by the AI until an agent takes over." },
};

const COUNTRIES = [
  ["GB", "United Kingdom"], ["IE", "Ireland"], ["US", "United States"], ["CA", "Canada"], ["AU", "Australia"],
  ["NZ", "New Zealand"], ["FR", "France"], ["DE", "Germany"], ["ES", "Spain"], ["IT", "Italy"], ["NL", "Netherlands"],
];

const blankChannel = () => ({
  DisplayName: "",
  UniqueIdentifier: "",
  DefaultCountryCode: "GB",
  AuthUsername: "",
  AuthPassword: "",
  DefaultAssignedUser_Id: "",
  AiMode: "OFF",
  AiKnowledgeData: "",
  CloseConversation_MessagingConversationTemplate_Id: "",
  BusinessHours: null,
  FlagAwayMessageActive: 0,
  AwayMessage: "",
  FlagActive: 1,
});

const fromRow = (row) => ({
  ...blankChannel(),
  Id: row.Id,
  DisplayName: row.DisplayName || "",
  UniqueIdentifier: row.UniqueIdentifier || "",
  DefaultCountryCode: row.DefaultCountryCode || "GB",
  DefaultAssignedUser_Id: row.DefaultAssignedUser_Id ? String(row.DefaultAssignedUser_Id) : "",
  AiMode: AI_MODES[row.AiMode] ? row.AiMode : "OFF",
  AiKnowledgeData: row.AiKnowledgeData || "",
  CloseConversation_MessagingConversationTemplate_Id: row.CloseConversation_MessagingConversationTemplate_Id
    ? String(row.CloseConversation_MessagingConversationTemplate_Id)
    : "",
  BusinessHours: normaliseBusinessHours(parseJson(row.BusinessHoursJson)),
  FlagAwayMessageActive: isOn(row.FlagAwayMessageActive) ? 1 : 0,
  AwayMessage: row.AwayMessage || "",
  FlagActive: row.FlagActive === null || row.FlagActive === undefined || isOn(row.FlagActive) ? 1 : 0,
});

const ChannelModal = ({ open, row, staff, templates, onClose, onSaved }) => {
  const [form, setForm] = useState(blankChannel());
  const [saving, setSaving] = useState(false);
  const isEdit = !!row?.Id;

  useEffect(() => {
    if (open) setForm(row ? fromRow(row) : blankChannel());
  }, [open, row]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const channelTemplates = useMemo(
    () =>
      isEdit
        ? templates.filter((t) => !t.MessagingChannel_Id || String(t.MessagingChannel_Id) === String(row.Id))
        : [],
    [templates, isEdit, row]
  );

  const hoursError = validateBusinessHours(form.BusinessHours);
  const canSave = form.DisplayName.trim() && form.UniqueIdentifier.trim() && !hoursError && !saving;

  const save = async () => {
    if (!canSave) return;
    const sid = form.AuthUsername.trim();
    if (sid && !/^AC[a-f0-9]{32}$/i.test(sid)) {
      toast.error("The Twilio Account SID should start with AC followed by 32 characters");
      return;
    }
    const payload = {
      DisplayName: form.DisplayName.trim(),
      UniqueIdentifier: form.UniqueIdentifier.trim(),
      DefaultCountryCode: form.DefaultCountryCode || "GB",
      DefaultAssignedUser_Id: form.DefaultAssignedUser_Id || null,
      AiMode: form.AiMode,
      AiKnowledgeData: form.AiKnowledgeData,
      CloseConversation_MessagingConversationTemplate_Id: form.CloseConversation_MessagingConversationTemplate_Id || null,
      BusinessHoursJson: form.BusinessHours ? JSON.stringify(form.BusinessHours) : null,
      FlagAwayMessageActive: form.FlagAwayMessageActive,
      AwayMessage: form.AwayMessage,
      FlagActive: form.FlagActive,
    };
    if (sid) payload.AuthUsername = sid;
    if (form.AuthPassword.trim()) payload.AuthPassword = form.AuthPassword.trim();
    if (isEdit) payload.Id = row.Id;

    setSaving(true);
    const res = await (isEdit ? updateChannel(payload) : createChannel(payload));
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not save the channel"));
      return;
    }
    toast.success(res.message || "Saved");
    if (res.warning) toast.warn(res.warning);
    onSaved();
  };

  const mode = AI_MODES[form.AiMode] || AI_MODES.OFF;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit WhatsApp channel" : "New WhatsApp channel"}
      subtitle="A WhatsApp sender in your Twilio account"
      icon="ri-whatsapp-line"
      headerAccent="emerald"
      size="lg"
      loading={saving}
      onSubmit={save}
      submitLabel={isEdit ? "Save changes" : "Create channel"}
      submitDisabled={!canSave}
    >
      <div className="wa-settings__section-title">Number</div>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="wa-ch-name">Display name</Label>
          <Input id="wa-ch-name" value={form.DisplayName} onChange={onInput("DisplayName")} placeholder="e.g. Main office" />
        </Col>
        <Col md={6}>
          <Label htmlFor="wa-ch-number">WhatsApp sending number</Label>
          <Input id="wa-ch-number" value={form.UniqueIdentifier} onChange={onInput("UniqueIdentifier")} placeholder="+44 7700 900123" />
          <FieldHelp>The number registered as a WhatsApp sender in Twilio.</FieldHelp>
        </Col>
        <Col md={6}>
          <Label htmlFor="wa-ch-country">Default country</Label>
          <Input id="wa-ch-country" type="select" value={form.DefaultCountryCode} onChange={onInput("DefaultCountryCode")}>
            {COUNTRIES.some(([c]) => c === form.DefaultCountryCode) ? null : (
              <option value={form.DefaultCountryCode}>{form.DefaultCountryCode}</option>
            )}
            {COUNTRIES.map(([c, label]) => (
              <option key={c} value={c}>
                {label} ({c})
              </option>
            ))}
          </Input>
          <FieldHelp>Used to read numbers typed without a country code (e.g. 07700...).</FieldHelp>
        </Col>
        <Col md={6}>
          <Label htmlFor="wa-ch-assign">Assign new conversations to</Label>
          <Input id="wa-ch-assign" type="select" value={form.DefaultAssignedUser_Id} onChange={onInput("DefaultAssignedUser_Id")}>
            <option value="">Nobody (unassigned)</option>
            {staff.map((s) => (
              <option key={s.Id} value={String(s.Id)}>
                {s.FullName || [s.FirstName, s.LastName].filter(Boolean).join(" ") || `Staff #${s.Id}`}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      <div className="wa-settings__section-title">Twilio credentials</div>
      <Row className="g-3 mb-3">
        <Col md={6}>
          <Label htmlFor="wa-ch-sid">Account SID</Label>
          <Input
            id="wa-ch-sid"
            value={form.AuthUsername}
            onChange={onInput("AuthUsername")}
            placeholder={isEdit && row.AuthUsernameMasked ? row.AuthUsernameMasked : "AC…"}
            autoComplete="off"
          />
          <FieldHelp>
            {isEdit && row.AuthUsernameMasked
              ? `Currently ${row.AuthUsernameMasked}. Leave blank to keep it.`
              : "From the Twilio console dashboard."}
          </FieldHelp>
        </Col>
        <Col md={6}>
          <Label htmlFor="wa-ch-token">Auth token</Label>
          <Input
            id="wa-ch-token"
            type="password"
            value={form.AuthPassword}
            onChange={onInput("AuthPassword")}
            placeholder={isEdit && isOn(row.FlagAuthConfigured) ? "•••••••• (configured)" : ""}
            autoComplete="new-password"
          />
          <FieldHelp>
            {isEdit && isOn(row.FlagAuthConfigured) ? (
              <>
                <i className="ri-shield-check-line text-success me-1" />
                Configured. Leave blank to keep the stored token.
              </>
            ) : (
              "Stored encrypted and never shown again."
            )}
          </FieldHelp>
        </Col>
      </Row>

      <div className="wa-settings__section-title">AI</div>
      <div className="wa-settings__choice mb-2" role="radiogroup" aria-label="AI mode">
        {Object.entries(AI_MODES).map(([code, m]) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={form.AiMode === code}
            className={form.AiMode === code ? "is-active" : ""}
            onClick={() => set("AiMode")(code)}
          >
            {m.label}
          </button>
        ))}
      </div>
      <FieldHelp>{mode.help}</FieldHelp>
      {form.AiMode !== "OFF" && (
        <div className="mt-3">
          <Label htmlFor="wa-ch-ai">What the AI should know</Label>
          <Input
            id="wa-ch-ai"
            type="textarea"
            rows={5}
            value={form.AiKnowledgeData}
            onChange={onInput("AiKnowledgeData")}
            placeholder="What the AI should know - opening hours, services, prices, policies"
          />
        </div>
      )}

      <div className="wa-settings__section-title mt-4">Closing conversations</div>
      <Label htmlFor="wa-ch-close">Template sent when a conversation is closed</Label>
      <Input
        id="wa-ch-close"
        type="select"
        value={form.CloseConversation_MessagingConversationTemplate_Id}
        onChange={onInput("CloseConversation_MessagingConversationTemplate_Id")}
        disabled={!isEdit}
      >
        <option value="">None</option>
        {channelTemplates.map((t) => (
          <option key={t.Id} value={String(t.Id)}>
            {t.Name}
            {t.ApprovalStatus && t.ApprovalStatus !== "approved" ? ` (${t.ApprovalStatus})` : ""}
          </option>
        ))}
      </Input>
      <FieldHelp>
        {isEdit
          ? "Staff can still choose not to send it when they close a conversation."
          : "Save the channel and add its templates first, then pick one here."}
      </FieldHelp>

      <div className="wa-settings__section-title mt-4">Business hours</div>
      <BusinessHoursEditor value={form.BusinessHours} onChange={set("BusinessHours")} disabled={saving} />
      {hoursError && <div className="text-danger small mt-2">{hoursError}</div>}

      <div className="mt-3">
        <Switch
          id="wa-ch-away"
          checked={isOn(form.FlagAwayMessageActive)}
          onChange={set("FlagAwayMessageActive")}
          label="Send an away message outside business hours"
        />
        {isOn(form.FlagAwayMessageActive) && (
          <div className="mt-2">
            <Input
              type="textarea"
              rows={3}
              aria-label="Away message"
              value={form.AwayMessage}
              onChange={onInput("AwayMessage")}
              placeholder="Thanks for your message. We are closed right now and will reply when we open."
            />
            <FieldHelp>
              Sent once per 12 hours when a customer messages outside business hours; not sent while AI is replying.
              {!form.BusinessHours && " Set business hours above, otherwise the number counts as always open."}
            </FieldHelp>
          </div>
        )}
      </div>

      <div className="wa-settings__section-title mt-4">Status</div>
      <Switch id="wa-ch-active" checked={isOn(form.FlagActive)} onChange={set("FlagActive")} label="Active" />
    </AppModal>
  );
};

ChannelModal.propTypes = {
  open: PropTypes.bool,
  row: PropTypes.object,
  staff: PropTypes.array.isRequired,
  templates: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const ChannelsTab = ({ channels, loading, reload }) => {
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, row = edit
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    listStaff().then((res) => {
      if (ok(res)) setStaff(res.staff_list || []);
    });
    listTemplates().then((res) => {
      if (ok(res)) setTemplates(res.messagingconversationtemplate_list || []);
    });
  }, []);

  const staffName = (id) => {
    const s = staff.find((x) => String(x.Id) === String(id));
    return s ? s.FullName || [s.FirstName, s.LastName].filter(Boolean).join(" ") : "";
  };

  const runTest = async (row) => {
    setTestingId(row.Id);
    const res = await testChannel(row.Id);
    setTestingId(null);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not reach Twilio"));
      setTestResult(null);
      return;
    }
    if (res.sender) toast.success(res.message || "Connected to Twilio");
    else toast.warn(res.message || "Connected to Twilio");
    setTestResult({ channel: row, webhook: res.webhook_url, sender: res.sender });
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(testResult.webhook);
      toast.success("Webhook URL copied");
    } catch (_) {
      toast.error("Could not copy - select the address and copy it by hand");
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    const res = await deleteChannel(deleting.Id);
    setDeleteBusy(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not delete the channel"));
      return;
    }
    toast.success(res.message || "Deleted");
    if (testResult?.channel?.Id === deleting.Id) setTestResult(null);
    setDeleting(null);
    reload();
  };

  const newButton = (
    <Button color="primary" size="sm" onClick={() => setEditing({})}>
      <i className="ri-add-line me-1" />
      New channel
    </Button>
  );

  return (
    <>
      <Toolbar note="Each channel is one WhatsApp sending number in your Twilio account.">{newButton}</Toolbar>

      <ListState
        loading={loading && !channels.length}
        empty={!channels.length}
        title="No WhatsApp numbers yet"
        text="Add the WhatsApp sender from your Twilio account, then test the connection to get the webhook address."
        action={newButton}
      >
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>Name</th>
                <th>Sending number</th>
                <th>Twilio account</th>
                <th>AI</th>
                <th>Default owner</th>
                <th>Status</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.Id}>
                  <td className="fw-medium">{c.DisplayName || <span className="text-muted">Unnamed</span>}</td>
                  <td className="text-nowrap">{c.UniqueIdentifier}</td>
                  <td className="text-nowrap">
                    <span className="me-2">{c.AuthUsernameMasked || <span className="text-muted">No SID</span>}</span>
                    {isOn(c.FlagAuthConfigured) ? (
                      <span className="badge bg-success-subtle text-success">Token set</span>
                    ) : (
                      <span className="badge bg-warning-subtle text-warning">No token</span>
                    )}
                  </td>
                  <td>{(AI_MODES[c.AiMode] || AI_MODES.OFF).label}</td>
                  <td>{staffName(c.DefaultAssignedUser_Id) || <span className="text-muted">Unassigned</span>}</td>
                  <td>
                    <ActiveBadge active={c.FlagActive === null || c.FlagActive === undefined ? 1 : c.FlagActive} />
                  </td>
                  <td className="text-end text-nowrap">
                    <Button
                      color="light"
                      size="sm"
                      className="me-1"
                      title="Test connection"
                      disabled={testingId === c.Id}
                      onClick={() => runTest(c)}
                    >
                      {testingId === c.Id ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <i className="ri-plug-line align-bottom" />
                      )}
                      <span className="ms-1 d-none d-lg-inline">Test</span>
                    </Button>
                    <Button color="light" size="sm" className="me-1" title="Edit" onClick={() => setEditing(c)}>
                      <i className="ri-pencil-fill align-bottom" />
                    </Button>
                    <Button color="light" size="sm" title="Delete" onClick={() => setDeleting(c)}>
                      <i className="ri-delete-bin-line align-bottom text-danger" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </ListState>

      {testResult && (
        <div className="wa-settings__callout mt-3">
          <div className="d-flex align-items-start gap-2">
            <i className="ri-links-line fs-5 text-primary" />
            <div className="flex-grow-1 min-w-0">
              <div className="fw-medium">
                Webhook for {testResult.channel.DisplayName || testResult.channel.UniqueIdentifier}
              </div>
              <div className="text-muted small mb-2">
                In Twilio, set this as the webhook for your WhatsApp sender / Conversations service (POST).
              </div>
              <div className="wa-settings__copy">
                <code>{testResult.webhook}</code>
                <Button color="light" size="sm" onClick={copyWebhook} title="Copy">
                  <i className="ri-file-copy-line" />
                </Button>
              </div>
            </div>
            <Button color="link" size="sm" className="p-0 text-muted" title="Dismiss" onClick={() => setTestResult(null)}>
              <i className="ri-close-line fs-5" />
            </Button>
          </div>
        </div>
      )}

      <ChannelModal
        open={!!editing}
        row={editing && editing.Id ? editing : null}
        staff={staff}
        templates={templates}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />

      <DeleteModal
        show={!!deleting}
        loading={deleteBusy}
        onCloseClick={() => setDeleting(null)}
        onDeleteClick={confirmDelete}
        customText={`Delete the WhatsApp channel "${deleting?.DisplayName || deleting?.UniqueIdentifier || ""}"? Nothing more can be sent or received on this number.`}
      />
    </>
  );
};

ChannelsTab.propTypes = {
  channels: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  reload: PropTypes.func.isRequired,
};

export default ChannelsTab;
